import { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { openLiveLinkWindow } from '../../../redux/buzzbridge/actions';
import { openingLiveLinkWindowSelector } from '../../../redux/buzzbridge/selectors';
import { hasActiveCampaignHostPlanSelector } from '../../../redux/campaignHostSubscription/selectors';
import { fetchCampaignHostPricing } from '../../../redux/campaignHostSubscription/actions';
import type { ICampaignHostPackage } from '../../../types/global/campaignHostSubscription.types';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { CampaignHostCheckoutDialog } from '../subscription/CampaignHostCheckoutDialog';
import { CampaignHostTierPickerModal } from '../subscription/CampaignHostTierPickerModal';
import { useCampaignHostCheckout } from '../subscription/useCampaignHostCheckout';

export function LiveLinkWindowBanner({
    show,
    windowStillOpen,
    expiresLabel,
    participantId,
    roundId,
}: {
    show: boolean;
    windowStillOpen: boolean;
    expiresLabel?: string;
    participantId: string;
    roundId?: string | null;
}) {
    const dispatch = useDispatch();
    const opening = useSelector(openingLiveLinkWindowSelector);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const [tierPickerOpen, setTierPickerOpen] = useState(false);

    const grant = () => {
        dispatch(openLiveLinkWindow({ participantId, roundId: roundId || null }));
    };

    const { checkoutOpen, setCheckoutOpen, beginMonthly, beginLtd, onPaymentMethodReady, checkoutTitle } =
        useCampaignHostCheckout({
            onSubscriptionComplete: () => {
                setTierPickerOpen(false);
                grant();
            },
        });

    if (!show) return null;

    const onClick = () => {
        if (!hasPlan) {
            dispatch(fetchCampaignHostPricing());
            setTierPickerOpen(true);
            return;
        }
        grant();
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: 'rgba(255,201,101,0.06)',
                }}>
                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.45 }}>
                    {windowStillOpen && expiresLabel
                        ? `This delivery is open until ${expiresLabel}. The creator can upload or submit the live link.`
                        : 'This delivery date has passed. Open a 24-hour window so the creator can upload or submit the live link. A host subscription is required.'}
                </Typography>
                <Button
                    size="small"
                    disabled={opening}
                    onClick={onClick}
                    sx={{
                        flexShrink: 0,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 12,
                        textTransform: 'none',
                        borderRadius: 999,
                        px: 1.75,
                        py: 0.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#131517',
                        '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                    }}>
                    {opening ? (
                        <CircularProgress size={14} sx={{ color: '#131517' }} />
                    ) : windowStillOpen ? (
                        'Extend 24 hours'
                    ) : hasPlan ? (
                        'Allow 24 hours'
                    ) : (
                        'Subscribe to allow 24 hours'
                    )}
                </Button>
            </Box>
            <CampaignHostTierPickerModal
                open={tierPickerOpen}
                onClose={() => setTierPickerOpen(false)}
                onSelectMonthly={(pkg: ICampaignHostPackage) => {
                    setTierPickerOpen(false);
                    beginMonthly(pkg);
                }}
                onSelectLtd={(pkg: ICampaignHostPackage) => {
                    setTierPickerOpen(false);
                    beginLtd(pkg);
                }}
            />
            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />
        </>
    );
}
